/* eslint-disable @typescript-eslint/naming-convention */
import { clamp } from '../Math.js';
/**
 * Utility class for creating and manipulating `IColor` objects.
 */
export class IColor {
    constructor(r, g, b) {
        if (r !== undefined && g !== undefined && b !== undefined) {
            return { r: r, g: g, b: b };
        }
        return { r: 0, g: 0, b: 0 };
    }
    /**
     * Type guard to check if the provided object is applicable to the `IColor` interface.
     * @returns Type assertion if the object is applicable to the IColor.
     */
    static is(unknown) {
        const color = unknown;
        return typeof color.r === 'number' && typeof color.g === 'number' && typeof color.b === 'number';
    }
    /**
     * Checks if two `IColor` objects are equal by comparing their components.
     * @returns True if the components of color1 and color2 are exactly equal.
     */
    static equals(color1, color2) {
        return color1.r === color2.r && color1.g === color2.g && color1.b === color2.b;
    }
    /**
     * Creates a new `IColor` object as a verbose copy of the provided color.
     * @returns A new `IColor` object.
     */
    static copy(color) {
        return new IColor(color.r, color.g, color.b);
    }
    /**
     * Copies the values from one `IColor` object to another.
     * @note This method directly modifies the `result` color.
     */
    static copy_to_ref(color, result) {
        result.r = color.r;
        result.g = color.g;
        result.b = color.b;
        return result;
    }
    /**
     * Directly sets the values to a `IColor` object.
     * @note This method directly modifies the `target` color.
     */
    static set(color, r, g, b) {
        color.r = r;
        color.g = g;
        color.b = b;
        return color;
    }
    /**
     * Creates a new `IColor` object from provided `hex` string.
     * @returns A new `IColor` object.
     */
    static from_HEX(hex) {
        hex = hex.replace('#', '');
        const v = parseInt(hex, 16);
        let b = v & 0xff;
        let g = (v >> 8) & 0xff;
        let r = (v >> 16) & 0xff;
        r /= 255.0;
        g /= 255.0;
        b /= 255.0;
        return { r: r, g: g, b: b };
    }
    /**
     * Creates a new `IColor` object from provided temperature in kelvin.
     * Conversion algorithm from http://www.tannerhelland.com/4435/convert-temperature-rgb-algorithm-code/
     * Returns an IColor in sRGB color space.
     * @returns A new `IColor` object.
     */
    static from_temperature(kelvin) {
        const temperature_scaled = kelvin / 100;
        let red, green, blue;
        if (temperature_scaled <= 66) {
            red = 255;
            green = temperature_scaled;
            green = 99.4708025861 * Math.log(green) - 161.1195681661;
            if (temperature_scaled <= 19) {
                blue = 0;
            }
            else {
                blue = temperature_scaled - 10;
                blue = 138.5177312231 * Math.log(blue) - 305.0447927307;
            }
        }
        else {
            red = temperature_scaled - 60;
            red = 329.698727446 * Math.pow(red, -0.1332047592);
            green = temperature_scaled - 60;
            green = 288.1221695283 * Math.pow(green, -0.0755148492);
            blue = 255;
        }
        return {
            r: clamp(red, 0, 255) / 255,
            g: clamp(green, 0, 255) / 255,
            b: clamp(blue, 0, 255) / 255
        };
    }
    /**
     * Converts provided `IVector3` to sRGB color space.
     * @returns A new `IColor` object.
     */
    static to_sRGB(vector) {
        return {
            r: Math.pow(vector.x, 1 / 2.2),
            g: Math.pow(vector.y, 1 / 2.2),
            b: Math.pow(vector.z, 1 / 2.2)
        };
    }
    /**
     * Converts provided `IColor` to Linear color space represented as `IVector3`.
     * @returns A new `IVector3` object.
     */
    static to_linear_space(color) {
        return {
            x: Math.pow(color.r, 2.2),
            y: Math.pow(color.g, 2.2),
            z: Math.pow(color.b, 2.2)
        };
    }
    /**
     * Verifies if the provided `IColor` is within the specified range.
     * @returns `true` if the color is within the range.
     */
    static is_in_range(color, min, max) {
        return !(color.r < min || color.r > max || color.g < min || color.g > max || color.b < min || color.b > max);
    }
    /**
     * Verifies if the provided `IColor` is within `0 - 1` range.
     * @returns `true` if the color is within the range.
     */
    static is_saturated(color) {
        return !(color.r < 0 || color.r > 1 || color.g < 0 || color.g > 1 || color.b < 0 || color.b > 1);
    }
    static saturate(color) {
        return new IColor(clamp(color.r, 0, 1), clamp(color.g, 0, 1), clamp(color.b, 0, 1));
    }
    static saturate_to_ref(color, result) {
        result.r = clamp(color.r, 0, 1);
        result.g = clamp(color.g, 0, 1);
        result.b = clamp(color.b, 0, 1);
        return result;
    }
    /**
     * Converts an `IColor` object to an array of numbers `[ r, g, b ]`.
     * @returns An array `[ number, number, number ]`.
     */
    static to_array(color) {
        return [color.r, color.g, color.b];
    }
    /**
     * Converts an `IColor` object to a `string`.
     * @returns A string in the format `{ r: number, g: number, b: number }`.
     */
    static to_string(color) {
        return `{ r: ${color.r}, g: ${color.g}, b: ${color.b} }`;
    }
}
//# sourceMappingURL=IColor.js.map